/* eslint-disable @typescript-eslint/no-explicit-any */
import { richTextFromMarkdown } from '@contentful/rich-text-from-markdown';
import {
  defaultLocale,
  localeMapper,
  localeToLanguageCodeMapper,
  type FormLocales,
} from '@quno/onboarding/src/constants/locales';
import { transformModelToBlocks } from '@quno/onboarding/src/generator/utils/transformModelToBlocks';
import type { Document } from '@contentful/rich-text-types';
import type { LanguageCode } from '@quno/onboarding/src/constants/locales';
import type {
  ModelName,
  ReviewContainer,
  TreatmentContainer,
  LogoContainer,
  BackgroundGroupColor,
  BackgroundGroupImage,
  BackgroundGroup,
  Cta,
  BeforeAfterContainer,
  HeroAndFunnel,
  ImageAndText,
  Calendar,
  FaqContainer,
  TextContainer,
  ImageContainer,
  ContainerTypes,
  CardContainer,
  CodeContainer,
} from '@quno/onboarding/src/generator/types/Models';
import type { Page } from '@quno/onboarding/src/generator/types/Models';
import type {
  BooleanAttribute,
  StringAttribute,
} from '@quno/onboarding/src/generator/types/generics/ModelAttributes';
import type { MultiLocale } from '@quno/onboarding/src/types';
import type {
  BlockContainerFragment,
  Page as PageFragment,
  BlockHeroFragment,
} from '@quno/patient-journey/src/contentful/generated/graphql-patient-journey';

export async function getRichTextMultilocale(
  attribute: StringAttribute,
  locale: FormLocales,
  cb?: (text: string) => string | undefined,
): Promise<Document | undefined> {
  const value = getAvailableLocaleString(attribute, locale);

  if (!value) {
    return undefined;
  }

  const processedText = cb ? cb(value) : value;

  const richText = await richTextFromMarkdown(processedText || '');
  return richText;
}

export function getStringMultilocale(
  attribute: StringAttribute,
  locale: FormLocales,
): string {
  return getAvailableLocaleString(attribute, locale);
}

export const getAvailableLocaleStringFromLanguageCode = <T>(
  attribute: MultiLocale<T> | undefined,
  locale: LanguageCode,
): string => {
  if (!attribute) {
    return '';
  }

  const localeKeys = Object.keys(localeMapper) as LanguageCode[];
  const availableString = localeKeys.reduce(
    (acc, l) => attribute[l] || acc,
    '' as T,
  );

  return (attribute?.[locale] || availableString || '') as string;
};

export const getAvailableLocaleString = (
  attribute: StringAttribute,
  locale: FormLocales,
): string => {
  const localeKeys = Object.keys(localeToLanguageCodeMapper);
  const availableString = localeKeys.reduce(
    (acc, l) => attribute?.[l as FormLocales] || acc,
    '',
  );

  return attribute?.[locale] || availableString || '';
};

export function getBooleanMultilocale(
  attribute: BooleanAttribute,
  locale: FormLocales,
): boolean {
  return attribute?.[locale] || attribute?.[defaultLocale] || false;
}

export const componentMapper: Partial<
  Record<ModelName, (props: any, locale: FormLocales, args?: any) => any>
> = {
  async ReviewContainer(
    reviewContainer: ReviewContainer,
    locale: FormLocales,
  ): Promise<ReviewContainer & { locale: FormLocales }> {
    return { ...reviewContainer, locale };
  },
  async TreatmentContainer(
    treatmentContainer: TreatmentContainer,
    locale: FormLocales,
  ): Promise<TreatmentContainer & { locale: FormLocales }> {
    return {
      ...treatmentContainer,
      locale,
    };
  },
  async Page(
    page: Page,
    locale: FormLocales,
  ): Promise<
    | PageFragment
    | (Page & {
        locale: FormLocales;
        isPreview?: boolean;
      })
  > {
    return {
      ...page,
      onboardingJson: page,
      isPreview: true,
      brand: {} as any,
      blocksCollection: {
        items: await transformModelToBlocks(
          componentMapper,
          locale,
          page.attributes?.pageBlocks?.[defaultLocale],
        ),
      } as any,
      locale,
    };
  },
  async LogoContainer(
    logoContainer: LogoContainer,
    locale: FormLocales,
  ): Promise<LogoContainer & { locale: FormLocales }> {
    return {
      ...logoContainer,
      locale,
    };
  },

  async Cta(
    cta: Cta,
    locale: FormLocales,
  ): Promise<Cta & { locale: FormLocales }> {
    return { ...cta, locale };
  },
  async ImageAndText(
    imageAndText: ImageAndText,
    locale: FormLocales,
  ): Promise<ImageAndText & { locale: FormLocales }> {
    return {
      ...imageAndText,
      locale,
    };
  },
  async HeroAndFunnel(
    heroAndFunnel: HeroAndFunnel,
    locale: FormLocales,
  ): Promise<HeroAndFunnel & { locale: FormLocales; isPreview?: boolean }> {
    return {
      ...heroAndFunnel,
      isPreview: true,
      locale,
    };
  },
  async BeforeAfterContainer(
    beforeAndAfterContainer: BeforeAfterContainer,
    locale: FormLocales,
  ): Promise<BeforeAfterContainer & { locale: FormLocales }> {
    return {
      ...beforeAndAfterContainer,
      locale,
    };
  },
  async Calendar(
    calendar: Calendar,
    locale: FormLocales,
  ): Promise<Calendar & { locale: FormLocales }> {
    return {
      ...calendar,
      locale,
    };
  },
  async FaqContainer(
    faqContainer: FaqContainer,
    locale: FormLocales,
  ): Promise<FaqContainer & { locale: FormLocales }> {
    return {
      ...faqContainer,
      locale,
    };
  },
  async TextContainer(
    textContainer: TextContainer,
    locale: FormLocales,
  ): Promise<TextContainer & { locale: FormLocales }> {
    return {
      ...textContainer,
      locale,
    };
  },
  async ImageContainer(
    imageContainer: ImageContainer,
    locale: FormLocales,
  ): Promise<ImageContainer & { locale: FormLocales }> {
    return {
      ...imageContainer,
      locale,
    };
  },
  async CardContainer(
    cardContainer: CardContainer,
    locale: FormLocales,
  ): Promise<CardContainer & { locale: FormLocales }> {
    return {
      ...cardContainer,
      locale,
    };
  },
  async CodeContainer(
    codeContainer: CodeContainer,
    locale: FormLocales,
  ): Promise<CodeContainer & { locale: FormLocales }> {
    return {
      ...codeContainer,
      locale,
    };
  },
};

export const getCardStyles = (
  container: Exclude<ContainerTypes, { model: 'LogoContainer' }>,
): CardStyles => {
  return {
    button: {
      backgroundColor:
        container.attributes?.cardStyles?.cardButtonStyles?.backgroundColor?.[
          defaultLocale
        ],
      fontColor:
        container.attributes?.cardStyles?.cardButtonStyles?.textColor?.[
          defaultLocale
        ],
    },
    card: {
      backgroundColor:
        container.attributes?.cardStyles?.backgroundColor?.[defaultLocale],
      fontColor: container.attributes?.cardStyles?.textColor?.[defaultLocale],
      borderRadius: borderRadiusMapper(
        container.attributes?.cardStyles?.borderRadius?.[defaultLocale],
      ),
    },
    image: {
      imageStyle:
        container.attributes?.cardStyles?.cardImageStyles?.imageStyle?.[
          defaultLocale
        ],
      imagePosition:
        container.attributes?.cardStyles?.cardImageStyles?.imagePosition?.[
          defaultLocale
        ],
      imageBorderRadius: borderRadiusMapper(
        container.attributes?.cardStyles?.cardImageStyles?.imageBorderRadius?.[
          defaultLocale
        ],
      ),
    },
  } as CardStyles;
};

export type BorderRadiusType =
  | 'small'
  | 'medium-small'
  | 'medium'
  | 'medium-large'
  | 'large';

type CardStyles = {
  button: {
    backgroundColor: string;
    fontColor: string;
  };
  card: {
    backgroundColor: string;
    fontColor: string;
    borderRadius: string;
  };
  image: {
    imageStyle: 'full' | 'icon' | 'circle';
    imagePosition: 'left' | 'center';
    imageBorderRadius: BorderRadiusType;
  };
};

export const borderRadiusMapper = (borderRadius?: string | null): string => {
  const obj: Record<BorderRadiusType, string> = {
    small: '5px',
    'medium-small': '15px',
    medium: '30px',
    'medium-large': '50px',
    large: '80px',
  };

  return obj[borderRadius as keyof typeof obj];
};

export const backgroundMapper = (
  backgroundGroup: BackgroundGroup['background'],
):
  | Partial<Record<keyof BlockContainerFragment | keyof BlockHeroFragment, any>>
  | undefined => {
  if (backgroundGroup?.style?.[defaultLocale] === 'color') {
    const { color, colorOpacity } = backgroundGroup as BackgroundGroupColor;
    return {
      backgroundColorOverwrite: color?.[defaultLocale],
      backgroundColorOpacity: colorOpacity?.[defaultLocale],
    };
  }

  if (backgroundGroup?.style?.[defaultLocale] === 'image') {
    const { image, overlay, opacity, backgroundStyle } =
      backgroundGroup as BackgroundGroupImage;

    return {
      backgroundStyle: backgroundStyle?.[defaultLocale],
      backgroundColorOpacity: opacity?.[defaultLocale],
      backgroundColorOverwrite: overlay?.[defaultLocale],
      image: {
        url: image?.[defaultLocale]?.url,
        width: 500,
        height: 600,
      },
      backgroundImage: {
        url: image?.[defaultLocale]?.url,
        width: 500,
        height: 600,
      },
    };
  }
};
